import { useContext, useState, type ReactElement } from "react";
import { useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";

import { CardCheckoutContext } from "./card-checkout.js";
import { LoadingPage } from "./loading-page.js";
import { tenantApi, tenantUrls } from "./urls.js";
import { useSend } from "./use-send.js";

/** 入力画面を開くのに要るもの（TenantCardViewModel）。 */
export interface TenantCardForm {
    readonly tenantId: number;
    readonly tenantTitle: string;
    readonly planDisplayName: string;
    /** PAY.JP の公開鍵（トークン化に使う。秘密鍵ではない）。 */
    readonly payjpPublicKey: string;
    /**
     * PAY.JP の定期課金が止まっている（前回のお支払いが済んでいない）ときの案内。止まっていなければ null。
     * 新しいカードでその場で払われることと金額を、カードを入力する前に伝える（Implem.Portal.Backend の docs/features/11 §5）。
     */
    readonly pausedNotice: string | null;
    /** カードを入力して送れるか。止まっていて画面からは再開できないときだけ false（お問い合わせへ）。 */
    readonly canSubmit: boolean;
    /** カードの入力を開くボタンの文言（サーバが決める）。 */
    readonly submitText: string;
}

const title = "カード情報の変更";

function CardForm({ value }: { readonly value: TenantCardForm }): ReactElement {
    usePageMeta(title);
    const form = useSend(tenantApi.card(value.tenantId));
    const renderCardCheckout = useContext(CardCheckoutContext);
    const [cardError, setCardError] = useState<string | undefined>();

    // カードの入力は申し込み・プラン変更と同じ PAY.JP のボタン（card-checkout.tsx。3-D セキュアは別窓）。
    // <b>ブラウザの確認ダイアログは出さない</b>——PAY.JP の入力画面の「変更する」が最後の確認になる（docs/01 §7）。
    // 止まった定期課金の再開でその場で払われることは、ボタンの前の案内（pausedNotice）で伝える
    const onToken = (token: string): void => {
        setCardError(undefined);
        form.send(new URLSearchParams({ CardToken: token }));
    };

    return (
        <div className="page max-w-2xl">
            <h1 className="page-title">{title}</h1>
            <p className="page-lead">{value.tenantTitle}（{value.planDisplayName}）</p>

            <div className="panel">
                <div>
                    <FormSummary messages={form.summary} />
                    {/* トークン化に失敗したときの理由。渡さないとカード拒否も 3-D セキュアの失敗も無反応になる */}
                    {cardError === undefined
                        ? null
                        : <div className="alert-danger mb-4" role="alert" data-payjp-error="">{cardError}</div>}

                    {value.pausedNotice === null
                        ? (
                            <p className="mb-7 text-sm text-slate-500">
                                お支払いに使うクレジットカードを差し替えます。<strong className="text-ink-800">プラン・料金は変わりません。</strong>
                                次回のお支払いから新しいカードに請求します。
                            </p>
                        )
                        : <div className="alert-warning mb-7" data-paused-notice="">{value.pausedNotice}</div>}

                    <div className="flex gap-3">
                        <a href={tenantUrls.list} className="btn-secondary">{value.canSubmit ? "キャンセル" : "戻る"}</a>
                        {value.canSubmit
                            ? renderCardCheckout({
                                publicKey: value.payjpPublicKey,
                                text: value.submitText,
                                submitText: "変更する",
                                disabled: form.busy,
                                onOpen: () => { setCardError(undefined); },
                                onToken,
                                onError: setCardError,
                            })
                            : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * カード情報の変更（<c>/Tenant/Card/{契約}</c>）。<b>プランを変えずに、支払いに使うカードだけを差し替える。</b>
 * 開けるのは<b>クレジットカード払いの契約の契約者だけ</b>——それ以外（請求書払い・無償プランも）は API が 404 を返す。
 */
export function CardPage(): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<TenantCardForm>(tenantApi.card(tenantId));

    return data.state === "ready"
        ? <CardForm value={data.value} />
        : <LoadingPage title={title} data={data} width="max-w-2xl" />;
}
